import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Utils
import { THEME, updateTheme } from './utils/theme';
import api from './utils/api';
import SoundService from './utils/sounds';
import './utils/animations'; // injects CSS animations on import

// Hooks
import useIsMobile from './hooks/useIsMobile';

// UI Components
import Spinner from './components/ui/Spinner';
import Avatar from './components/ui/Avatar';

// Layout Components
import MobileBottomNav from './components/layout/MobileBottomNav';
import MobileMenu from './components/layout/MobileMenu';
import MobileStatusPicker from './components/layout/MobileStatusPicker';
import MobileFilterSheet from './components/layout/MobileFilterSheet';
import SyncProgress from './components/layout/SyncProgress';
import ToastNotification from './components/layout/ToastNotification';
import FilterDrawer from './components/layout/FilterDrawer';
import ViewToggle from './components/layout/ViewToggle';
import BulkActions from './components/layout/BulkActions';

// Auth
import LoginScreen from './components/auth/LoginScreen';

// Game Components
import GameCard from './components/game/GameCard';
import GameDetailModal from './components/game/GameDetailModal';
import AddSessionModal from './components/game/AddSessionModal';
import AddGameModal from './components/game/AddGameModal';

// Stats
import StatsDashboard from './components/stats/StatsDashboard';

// Squad
import SquadChatModal from './components/squad/SquadChatModal';
import SquadManagementModal from './components/squad/SquadManagementModal';

// Social
import NotificationsModal from './components/social/NotificationsModal';
import UserDiscoveryModal from './components/social/UserDiscoveryModal';
import UserProfileModal from './components/social/UserProfileModal';
import FriendsActivityModal from './components/social/FriendsActivityModal';
import AutoSessionsBanner from './components/social/AutoSessionsBanner';

// Discovery
import LastTenPlayedModal from './components/discovery/LastTenPlayedModal';
import TopTenModal from './components/discovery/TopTenModal';
import RecentlyAddedModal from './components/discovery/RecentlyAddedModal';
import HiddenGemsModal from './components/discovery/HiddenGemsModal';
import RateUnratedModal from './components/discovery/RateUnratedModal';
import RatingComparisonModal from './components/discovery/RatingComparisonModal';
import GenreBreakdownModal from './components/discovery/GenreBreakdownModal';
import YearReviewModal from './components/discovery/YearReviewModal';

// Picker
import GameNightPicker from './components/picker/GameNightPicker';
import GameNightSchedulerModal from './components/picker/GameNightSchedulerModal';
import QueueModal from './components/picker/QueueModal';
import SmartSuggestModal from './components/picker/SmartSuggestModal';
import BacklogBurndownModal from './components/picker/BacklogBurndownModal';

// Settings
import SettingsModal from './components/settings/SettingsModal';

// Help
import HelpCenterModal from './components/help/HelpCenterModal';
import KeyboardHelpModal from './components/help/KeyboardHelpModal';
import CommandPalette from './components/help/CommandPalette';
import TourOverlay, { TOUR_STEPS } from './components/help/TourOverlay';

// Alias used in App
const RandomPicker = GameNightPicker;

export default function App() {
  const isMobile = useIsMobile();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [totalGames, setTotalGames] = useState(0);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Dynamic user-based filters (keyed by username)
  const [userStatusFilters, setUserStatusFilters] = useState({});  // { username: ['played', 'wtp'], ... }
  const [userFavoritesFilters, setUserFavoritesFilters] = useState({});  // { username: true, ... }
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('playtime');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [controllerFilter, setControllerFilter] = useState('');
  const [tagFilter, setTagFilter] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [recentRecs, setRecentRecs] = useState({});
  const [steamStatus, setSteamStatus] = useState({});
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notificationsTab, setNotificationsTab] = useState('activity');
  const [unratedOnly, setUnratedOnly] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [sessionGame, setSessionGame] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('viewMode') || 'cards');
  const [showSettings, setShowSettings] = useState(false);
  const [detailGame, setDetailGame] = useState(null);
  const [selectedGames, setSelectedGames] = useState(new Set());
  const [savedCredentials, setSavedCredentials] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [libraryMode, setLibraryMode] = useState('coop');
  const [userRecommendationsFilters, setUserRecommendationsFilters] = useState({});  // { username: true, ... }
  // Squad context for multi-user support
  const [squads, setSquads] = useState([]);
  const [currentSquad, setCurrentSquad] = useState(null);
  const [squadMembers, setSquadMembers] = useState([]);
  const [showSquadChat, setShowSquadChat] = useState(false);
  const [showSquadManager, setShowSquadManager] = useState(false);
  const [creatingVoiceHeader, setCreatingVoiceHeader] = useState(false);
  const [showStatsDashboard, setShowStatsDashboard] = useState(false);
  const [showAddGame, setShowAddGame] = useState(false);
  // Mobile-specific state
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileStatusPicker, setShowMobileStatusPicker] = useState(false);
  const [mobileNavTab, setMobileNavTab] = useState('home');
  const [prevSteamStatus, setPrevSteamStatus] = useState({});
  // Activity feed state
  const [activities, setActivities] = useState([]);
  const [activityCount, setActivityCount] = useState(0);
  const [showActivityDropdown, setShowActivityDropdown] = useState(false);
  const [showPresetDropdown, setShowPresetDropdown] = useState(false);
  // Direct messages state
  const [directMessages, setDirectMessages] = useState([]);
  const [dmUnreadCount, setDmUnreadCount] = useState(0);
  const [showDMModal, setShowDMModal] = useState(false);
  // New UI state
  const [statusTab, setStatusTab] = useState('all');
  const [lfgMode, setLfgMode] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [toastedActivityIds, setToastedActivityIds] = useState(new Set());
  // Discovery & Insights modal state
  const [showLastTenPlayed, setShowLastTenPlayed] = useState(false);
  const [showTopTen, setShowTopTen] = useState(false);
  const [showRecentlyAdded, setShowRecentlyAdded] = useState(false);
  const [showHiddenGems, setShowHiddenGems] = useState(false);
  const [showRateUnrated, setShowRateUnrated] = useState(false);
  const [showRatingComparison, setShowRatingComparison] = useState(false);
  const [showGenreBreakdown, setShowGenreBreakdown] = useState(false);
  const [showYearReview, setShowYearReview] = useState(false);
  const [yearReviewYear, setYearReviewYear] = useState(new Date().getFullYear());
  const [showDiscoverMenu, setShowDiscoverMenu] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  // Phase 2 features state
  const [pinnedGames, setPinnedGames] = useState([]);
  const [queue, setQueue] = useState([]);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [showBacklogBurndown, setShowBacklogBurndown] = useState(false);
  // New feature state
  const [showSmartSuggest, setShowSmartSuggest] = useState(false);
  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [showUserDiscovery, setShowUserDiscovery] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [filterPresets, setFilterPresets] = useState(() => {
    try { return JSON.parse(localStorage.getItem('filterPresets') || '[]'); } catch { return []; }
  });
  const [undoAction, setUndoAction] = useState(null);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showGameNightScheduler, setShowGameNightScheduler] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  const [twitchStreams, setTwitchStreams] = useState([]);
  const [showTwitchPanel, setShowTwitchPanel] = useState(false);
  // New integration features
  const [showAchievementRaces, setShowAchievementRaces] = useState(false);
  const [showWishlist, setShowWishlist] = useState(false);
  const [showFriendsPlaying, setShowFriendsPlaying] = useState(false);
  const [showFriendsActivity, setShowFriendsActivity] = useState(false);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [friendsPlaying, setFriendsPlaying] = useState([]);
  const [achievementRaces, setAchievementRaces] = useState([]);
  const [activeRemotePlay, setActiveRemotePlay] = useState([]);

  // Poll for active Remote Play sessions
  useEffect(() => {
    if (!user) return;
    const checkRemotePlay = async () => {
      try {
        const data = await api.get('/api/remote-play/active');
        setActiveRemotePlay(data.sessions || []);
      } catch (e) { /* ignore */ }
    };
    checkRemotePlay();
    const interval = setInterval(checkRemotePlay, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => { localStorage.setItem('viewMode', viewMode); }, [viewMode]);

  useEffect(() => {
    // The session lives in an httpOnly cookie, so confirm it by calling /api/me.
    // loadCredentials() only restores the remembered username for the UI.
    const saved = api.loadCredentials();
    if (saved) setSavedCredentials(saved);
    api.get('/api/me').then(user => {
      setUser(user);
      // Show tour for first-time users
      if (!localStorage.getItem('tourCompleted')) setShowTour(true);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Load the user's squads whenever we have a user — covers BOTH a fresh login and a
  // page refresh. Previously the squad load lived inside the /api/me path, so a fresh
  // login (which sets the user without re-running that effect) showed no squad until reload.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const userSquads = await api.get('/api/squads');
        if (cancelled) return;
        setSquads(userSquads);
        // Set default squad (founder squad first, or first available)
        const founderSquad = userSquads.find(s => s.is_founder_squad);
        const defaultSquad = founderSquad || userSquads[0];
        if (defaultSquad) {
          setCurrentSquad(defaultSquad);
          const squadDetails = await api.get(`/api/squads/${defaultSquad.id}`);
          if (!cancelled) setSquadMembers(squadDetails.members || []);
        }
      } catch (err) {
        console.error('Failed to load squads:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K / Ctrl+K opens command palette from anywhere
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
        return;
      }
      // Skip other shortcuts when in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (e.key === 'r' || e.key === 'R') setShowPicker(true);
      if (e.key === '/' || e.key === 's') { e.preventDefault(); document.querySelector('input[placeholder*="Search"]')?.focus(); }
      if (e.key === 'f' || e.key === 'F') setShowFilters(true);
      if (e.key === 'd' || e.key === 'D') setShowStatsDashboard(true);
      if (e.key === 'l' || e.key === 'L') setLfgMode(prev => !prev);
      if (e.key === 'w' || e.key === 'W') setShowSmartSuggest(true);
      if (e.key === '?') setShowKeyboardHelp(true);
      if (e.key === 'h' || e.key === 'H') setShowHelpCenter(true);
      if (e.key === 'Escape') { setShowPicker(false); setDetailGame(null); setShowSettings(false); setShowFilters(false); setShowStatsDashboard(false); setSelectedGames(new Set()); setLfgMode(false); setShowKeyboardHelp(false); setShowHelpCenter(false); setShowSmartSuggest(false); setShowSquadChat(false); setShowSquadManager(false); setShowUserDiscovery(false); setShowCommandPalette(false); setShowPresetDropdown(false); setShowAddGame(false); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Debounce search input to prevent race conditions
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300); // 300ms delay
    return () => clearTimeout(timer);
  }, [search]);

  const GAMES_LIMIT = 1000;

  const buildGamesParams = useCallback(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (lfgMode) {
      params.set('lfg', 'true');
    } else if (statusTab && statusTab !== 'all') {
      params.set('status_tab', statusTab);
    } else {
      // Dynamic user status filters - backward compatible with omega/supe
      Object.entries(userStatusFilters).forEach(([username, statuses]) => {
        if (statuses && statuses.length > 0) {
          if (username === 'omega') params.set('status_omega', statuses.join(','));
          else if (username === 'supe') params.set('status_supe', statuses.join(','));
          else params.set(`status_${username}`, statuses.join(','));
        }
      });
    }
    // Dynamic favorites filters
    Object.entries(userFavoritesFilters).forEach(([username, enabled]) => {
      if (enabled) {
        if (username === 'omega') params.set('favorite_omega', 'true');
        else if (username === 'supe') params.set('favorite_supe', 'true');
        else params.set(`favorite_${username}`, 'true');
      }
    });
    // Dynamic recommendations filters
    Object.entries(userRecommendationsFilters).forEach(([username, enabled]) => {
      if (enabled) {
        if (username === 'omega') params.set('recommended_omega', 'true');
        else if (username === 'supe') params.set('recommended_supe', 'true');
        else params.set(`recommended_${username}`, 'true');
      }
    });
    if (typeFilter !== 'all') params.set('multiplayer_type', typeFilter);
    if (onlineOnly) params.set('online_only', 'true');
    if (controllerFilter) params.set('controller_support', controllerFilter);
    if (tagFilter.length > 0) params.set('tags', tagFilter.join(','));
    if (unratedOnly) params.set('unrated', 'true');
    if (libraryMode !== 'coop') params.set('library_mode', libraryMode);
    params.set('sort_by', sortBy);
    return params;
  }, [debouncedSearch, userStatusFilters, userFavoritesFilters, userRecommendationsFilters, typeFilter, sortBy, onlineOnly, controllerFilter, tagFilter, unratedOnly, libraryMode, statusTab, lfgMode]);

  const loadGames = useCallback(async () => {
    try {
      const params = buildGamesParams();
      params.set('limit', String(GAMES_LIMIT));
      const data = await api.get(`/api/games?${params}`);
      setGames(data.games);
      setTotalGames(data.total);
    } catch (err) { console.error('Failed to load games:', err); }
  }, [buildGamesParams]);

  const loadStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (libraryMode !== 'coop') params.set('library_mode', libraryMode);
      const url = params.toString() ? `/api/stats?${params}` : '/api/stats';
      setStats(await api.get(url));
    } catch (err) {}
  }, [libraryMode]);
  const loadSyncStatus = useCallback(async () => { try { const status = await api.get('/api/sync/status'); setSyncStatus(status); return status; } catch (err) { return null; } }, []);
  const loadAllTags = useCallback(async () => { try { setAllTags(await api.get('/api/tags/all')); } catch (err) {} }, []);
  const loadRecentRecs = useCallback(async () => { try { setRecentRecs(await api.get('/api/recommendations/recent')); } catch (err) {} }, []);
  const loadSteamStatus = useCallback(async () => { try { setSteamStatus(await api.get('/api/steam/status')); } catch (err) {} }, []);
  const loadTwitchStatus = useCallback(async () => { try { const data = await api.get('/api/integrations/twitch/streams'); if (data.available !== false) setTwitchStreams(data.streams || []); } catch (err) {} }, []);
  // Phase 2 feature loaders
  const loadPinnedGames = useCallback(async () => { try { setPinnedGames(await api.get('/api/pins')); } catch (err) {} }, []);
  const loadQueue = useCallback(async () => { try { setQueue(await api.get('/api/queue')); } catch (err) {} }, []);
  const togglePin = async (gameId) => {
    const isPinned = pinnedGames.some(p => p.game_id === gameId);
    try {
      if (isPinned) {
        await api.delete(`/api/pins/${gameId}`);
      } else {
        await api.post(`/api/pins/${gameId}`);
      }
      loadPinnedGames();
    } catch (err) { alert(err.message); }
  };
  const addToQueue = async (gameId) => {
    try {
      await api.post(`/api/queue/${gameId}`);
      loadQueue();
    } catch (err) { alert(err.message); }
  };
  const removeFromQueue = async (gameId) => {
    try {
      await api.delete(`/api/queue/${gameId}`);
      loadQueue();
    } catch (err) { alert(err.message); }
  };

  // Activity feed functions
  const loadActivities = useCallback(async () => { try { setActivities(await api.get('/api/activities')); } catch (err) {} }, []);
  const loadActivityCount = useCallback(async () => { try { const data = await api.get('/api/activities/count'); setActivityCount(data.count); } catch (err) {} }, []);
  const markActivitiesSeen = async () => {
    try {
      await api.post('/api/activities/mark-seen');
      setActivityCount(0);
      setActivities(prev => prev.map(a => ({ ...a, read: true })));
    } catch (err) {}
  };
  const markActivityRead = async (activityId) => {
    try {
      await api.post(`/api/activities/${activityId}/read`);
      setActivities(prev => prev.map(a => a.id === activityId ? { ...a, read: true } : a));
      setActivityCount(prev => Math.max(0, prev - 1));
    } catch (err) {}
  };
  const handleActivityClick = async (activity) => {
    // Mark as read
    if (!activity.read) {
      await markActivityRead(activity.id);
    }
    // Navigate to the game
    const game = games.find(g => g.id === activity.game_id);
    if (game) setDetailGame(game);
    // Close dropdown
    setShowActivityDropdown(false);
  };
  const handleMatchStatus = async (activity) => {
    if (!activity.game_id || !activity.details?.status) return;
    try {
      await api.put(`/api/games/${activity.game_id}/status`, { status: activity.details.status });
      // Mark activity as read
      if (!activity.read) {
        await markActivityRead(activity.id);
      }
      // Remove from toasts if present
      setToasts(prev => prev.filter(t => t.id !== activity.id));
      // Reload games to reflect change
      loadGames();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };
  // Toast notification handlers
  const dismissToast = (activityId) => {
    setToasts(prev => prev.filter(t => t.id !== activityId));
    markActivityRead(activityId);
  };
  const handleToastAction = (toast, action) => {
    if (action === 'match') {
      handleMatchStatus(toast);
    } else if (toast.undoFn) {
      toast.undoFn();
    }
    dismissToast(toast.id);
  };

  // Direct message functions
  const loadDirectMessages = useCallback(async () => { try { setDirectMessages(await api.get('/api/dm')); } catch (err) {} }, []);
  const loadDMUnreadCount = useCallback(async () => { try { const data = await api.get('/api/dm/unread-count'); setDmUnreadCount(data.count); } catch (err) {} }, []);
  const markDMsRead = async () => { try { await api.post('/api/dm/mark-read'); setDmUnreadCount(0); } catch (err) {} };
  const sendDirectMessage = async (message) => {
    try {
      const msg = await api.post('/api/dm', { message });
      setDirectMessages(prev => [...prev, msg]);
      return msg;
    } catch (err) { alert('Failed to send message: ' + err.message); return null; }
  };

  useEffect(() => { if (user) { loadGames(); loadStats(); loadSyncStatus(); loadActivityCount(); loadDMUnreadCount(); loadAllTags(); loadRecentRecs(); loadSteamStatus(); loadTwitchStatus(); loadPinnedGames(); loadQueue(); } }, [user, loadGames, loadStats, loadSyncStatus, loadActivityCount, loadDMUnreadCount, loadAllTags, loadRecentRecs, loadSteamStatus, loadTwitchStatus, loadPinnedGames, loadQueue]);

  // Reload stats when library mode changes
  useEffect(() => { if (user) loadStats(); }, [libraryMode, loadStats, user]);

  // Poll for steam status every 3 minutes and show toast when someone starts playing
  useEffect(() => {
    if (!user) return;
    const pollSteamAndNotify = async () => {
      try {
        const newStatus = await api.get('/api/steam/status');
        // Check each squad member for new game activity
        squadMembers.forEach((member, idx) => {
          const memberStatus = newStatus[member.username];
          const prevMemberStatus = prevSteamStatus[member.username];
          if (memberStatus?.current_game_name && !prevMemberStatus?.current_game_name) {
            if (user.username !== member.username) {
              setToasts(prev => [...prev, {
                id: Date.now() + idx,
                type: 'now_playing',
                user: { display_name: member.display_name, color: member.theme_color || THEME.success },
                game_name: memberStatus.current_game_name
              }]);
            }
          }
        });
        setPrevSteamStatus(newStatus);
        setSteamStatus(newStatus);
      } catch (err) {}
    };
    const interval = setInterval(pollSteamAndNotify, 180000); // 3 minutes
    return () => clearInterval(interval);
  }, [user, prevSteamStatus, squadMembers]);

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (!user) return;
    let ws = null;
    let reconnectTimeout = null;

    const connect = () => {
      // Auth is via the httpOnly session cookie, sent automatically on the same-origin
      // handshake — no credentials in the URL (which used to leak into access logs).
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/ws/notifications`;

      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected for real-time notifications');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('Real-time notification:', message);
          // Handle different notification types
          if (message.type === 'activity' || message.type === 'status_change' || message.type === 'status_match' || message.type === 'mention') {
            // Refresh activity count and list
            loadActivityCount();
            loadActivities();
          } else if (message.type === 'dm') {
            // Refresh DM count
            loadDMUnreadCount();
          }
        } catch (err) {
          console.error('WebSocket message parse error:', err);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected, reconnecting in 5s...');
        reconnectTimeout = setTimeout(connect, 5000);
      };

      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
        ws.close();
      };

      // Send ping every 30 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      return () => clearInterval(pingInterval);
    };

    connect();

    return () => {
      if (ws) ws.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [user, loadActivityCount, loadActivities, loadDMUnreadCount]);

  // Poll for Twitch stream status every 2 minutes
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(loadTwitchStatus, 120000); // 2 minutes
    return () => clearInterval(interval);
  }, [user, loadTwitchStatus]);

  // Poll for new activity/DM counts every 30 seconds and show toasts for new status changes
  useEffect(() => {
    if (!user) return;
    const pollAndShowToasts = async () => {
      loadActivityCount();
      loadDMUnreadCount();
      // Fetch activities to check for new status changes to toast
      try {
        const newActivities = await api.get('/api/activities');
        // Find unread status_change activities we haven't toasted yet
        const newStatusChanges = newActivities.filter(
          a => a.activity_type === 'status_change' && !a.read && !toastedActivityIds.has(a.id)
        );
        if (newStatusChanges.length > 0) {
          setToasts(prev => [...newStatusChanges.slice(0, 3 - prev.length), ...prev].slice(0, 3));
          setToastedActivityIds(prev => new Set([...prev, ...newStatusChanges.map(a => a.id)]));
          // Auto-dismiss toasts after 10 seconds
          newStatusChanges.forEach(activity => {
            setTimeout(() => {
              setToasts(prev => prev.filter(t => t.id !== activity.id));
            }, 10000);
          });
        }
        setActivities(newActivities);
      } catch (err) {}
    };
    const interval = setInterval(pollAndShowToasts, 30000);
    return () => clearInterval(interval);
  }, [user, loadActivityCount, loadDMUnreadCount, toastedActivityIds]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post('/api/sync');
      const poll = async () => {
        const status = await loadSyncStatus();
        if (status && (status.status === 'running' || status.status === 'queued')) setTimeout(poll, 2000);
        else { setSyncing(false); loadGames(); loadStats(); }
      };
      poll();
    } catch (err) { alert('Sync failed: ' + err.message); setSyncing(false); loadSyncStatus(); }
  };


  const loadSmartSuggestions = async () => {
    try {
      const data = await api.get('/api/games/smart-suggest?limit=5');
      setSmartSuggestions(data.suggestions || []);
    } catch (err) { console.error('Failed to load smart suggestions:', err); }
  };

  const handleDiscover = (key) => {
    switch (key) {
      case 'lastTen': setShowLastTenPlayed(true); break;
      case 'topTen': setShowTopTen(true); break;
      case 'recentlyAdded': setShowRecentlyAdded(true); break;
      case 'hiddenGems': setShowHiddenGems(true); break;
      case 'rateUnrated': setShowRateUnrated(true); break;
      case 'ratingComparison': setShowRatingComparison(true); break;
      case 'genreBreakdown': setShowGenreBreakdown(true); break;
      case 'yearReview': setShowYearReview(true); break;
      case 'queue': setShowQueueModal(true); break;
      case 'backlogBurndown': setShowBacklogBurndown(true); break;
      case 'smartSuggest': loadSmartSuggestions(); setShowSmartSuggest(true); break;
    }
  };

  // Filter presets functions
  const saveFilterPreset = (name) => {
    const preset = {
      id: Date.now(),
      name,
      filters: {
        userStatusFilters, userFavoritesFilters, userRecommendationsFilters,
        typeFilter, sortBy, onlineOnly, controllerFilter, tagFilter, unratedOnly, statusTab, lfgMode
      }
    };
    const updated = [...filterPresets, preset];
    setFilterPresets(updated);
    localStorage.setItem('filterPresets', JSON.stringify(updated));
    addToast('success', `Preset "${name}" saved`);
  };

  const loadFilterPreset = (preset) => {
    const f = preset.filters;
    // Support both old and new format
    if (f.userStatusFilters) {
      setUserStatusFilters(f.userStatusFilters);
    } else {
      // Legacy format conversion
      const legacyStatus = {};
      if (f.omegaStatusFilter?.length) legacyStatus.omega = f.omegaStatusFilter;
      if (f.supeStatusFilter?.length) legacyStatus.supe = f.supeStatusFilter;
      setUserStatusFilters(legacyStatus);
    }
    if (f.userFavoritesFilters) {
      setUserFavoritesFilters(f.userFavoritesFilters);
    } else {
      const legacyFav = {};
      if (f.omegaFavoritesFilter) legacyFav.omega = true;
      if (f.supeFavoritesFilter) legacyFav.supe = true;
      setUserFavoritesFilters(legacyFav);
    }
    if (f.userRecommendationsFilters) {
      setUserRecommendationsFilters(f.userRecommendationsFilters);
    } else {
      const legacyRec = {};
      if (f.omegaRecommendationsFilter) legacyRec.omega = true;
      if (f.supeRecommendationsFilter) legacyRec.supe = true;
      setUserRecommendationsFilters(legacyRec);
    }
    setTypeFilter(f.typeFilter || 'all');
    setSortBy(f.sortBy || 'playtime');
    setOnlineOnly(f.onlineOnly || false);
    setControllerFilter(f.controllerFilter || '');
    setTagFilter(f.tagFilter || []);
    setUnratedOnly(f.unratedOnly || false);
    setStatusTab(f.statusTab || 'all');
    setLfgMode(f.lfgMode || false);
    addToast('success', `Loaded preset "${preset.name}"`);
  };

  const deleteFilterPreset = (id) => {
    const updated = filterPresets.filter(p => p.id !== id);
    setFilterPresets(updated);
    localStorage.setItem('filterPresets', JSON.stringify(updated));
  };

  // Toast helper with undo support
  const addToast = (type, message, undoFn = null, undoLabel = 'Undo') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, type, message, undoFn, undoLabel }]);
    // Play sound based on type
    if (type === 'success') SoundService.success();
    else if (type === 'error') SoundService.error();
    else if (type === 'now_playing') SoundService.notification();
    else SoundService.notification();
    // Auto-dismiss after 5 seconds
    setTimeout(() => dismissToast(id), 5000);
  };

  const handleToggleRecommendation = async (gameId) => {
    try {
      await api.put(`/api/games/${gameId}/recommend`);
      loadGames();
      loadRecentRecs();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const handleBulkStatus = async (newStatus) => {
    if (selectedGames.size === 0) return;
    try { await api.post('/api/games/bulk-status', { game_ids: Array.from(selectedGames), status: newStatus }); setSelectedGames(new Set()); loadGames(); loadStats(); }
    catch (err) { alert('Failed: ' + err.message); }
  };

  const toggleGameSelect = (gameId) => setSelectedGames(prev => { const next = new Set(prev); if (next.has(gameId)) next.delete(gameId); else next.add(gameId); return next; });
  const handleLogout = () => { api.clearCredentials(); setUser(null); setGames([]); setStats(null); setSavedCredentials(null); };
  const formatPlaytimeHours = (minutes) => Math.round(minutes / 60);

  const handleTourNext = () => {
    if (tourStep < TOUR_STEPS.length - 1) setTourStep(tourStep + 1);
    else { setShowTour(false); localStorage.setItem('tourCompleted', 'true'); }
  };
  const handleTourSkip = () => { setShowTour(false); localStorage.setItem('tourCompleted', 'true'); };

  const activeFilterCount = Object.values(userFavoritesFilters).filter(Boolean).length + Object.values(userRecommendationsFilters).filter(Boolean).length + (typeFilter !== 'all' ? 1 : 0) + (onlineOnly ? 1 : 0) + (controllerFilter ? 1 : 0) + (tagFilter.length > 0 ? 1 : 0) + (unratedOnly ? 1 : 0);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: THEME.bg }}>
      <div style={{ textAlign: 'center' }}>
        <Spinner size={40} />
        <p style={{ color: THEME.textMuted, marginTop: '16px' }}>Loading...</p>
      </div>
    </div>
  );

  if (!user) return <LoginScreen onLogin={u => { setUser(u); setSavedCredentials(api.loadCredentials()); if (!localStorage.getItem('tourCompleted')) setShowTour(true); }} />;

  const myColor = user.theme_color || THEME.success;

  return (
    <div style={{ minHeight: '100vh', background: `radial-gradient(ellipse at top, ${THEME.bgElevated} 0%, ${THEME.bg} 40%, #050508 100%)`, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: THEME.text, paddingBottom: isMobile ? '80px' : 0 }}>
      {/* Header - Responsive */}
      <header className="glass" style={{ padding: isMobile ? '12px 16px' : '16px 24px', borderBottom: `1px solid ${THEME.border}`, position: 'sticky', top: 0, zIndex: 100 }}>
        {/* Mobile: Hamburger + Logo + Notifications */}
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Top row: Hamburger + Logo + Notifications */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="press-effect" style={{ background: showMobileMenu ? THEME.bgHover : 'transparent', border: 'none', borderRadius: '8px', padding: '10px', cursor: 'pointer', fontSize: '20px' }}>
                {showMobileMenu ? '✕' : '☰'}
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src="/icons/icon.png" alt="" style={{ height: '48px', width: 'auto' }} />
                <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '800', background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>jasons.fun</h1>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Combined Notifications */}
                <button onClick={() => { setShowNotificationsModal(true); loadActivities(); loadDirectMessages(); }} className="press-effect" style={{ background: (activityCount + dmUnreadCount) > 0 ? `linear-gradient(135deg, ${THEME.omega}20, ${THEME.supe}20)` : 'transparent', border: 'none', borderRadius: '8px', padding: '8px', fontSize: '18px', cursor: 'pointer', position: 'relative' }}>
                  📬
                  {(activityCount + dmUnreadCount) > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-2px', background: THEME.danger, color: '#fff', fontSize: '9px', fontWeight: '700', padding: '2px 5px', borderRadius: '8px', minWidth: '14px', textAlign: 'center' }}>{(activityCount + dmUnreadCount) > 9 ? '9+' : (activityCount + dmUnreadCount)}</span>}
                </button>
              </div>
            </div>
            {/* Bottom row: Status Selector + Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
              {/* Status Tab Slideout Trigger */}
              {(() => {
                const STATUS_TABS = [
                  { value: 'all', label: 'All', count: stats?.total_games || 0 },
                  { value: 'played', label: 'Played', count: stats?.played_games || 0, color: THEME.success },
                  { value: 'wtp', label: 'WTP', count: stats?.wtp_games || 0, color: THEME.warning },
                  { value: 'shelved', label: 'Shelved', count: stats?.shelved_games || 0, color: THEME.textSecondary },
                  { value: 'never', label: 'Never', count: stats?.never_games || 0, color: THEME.danger },
                  { value: 'unplayed', label: 'Unplayed', count: stats?.unplayed_games || 0, color: THEME.textMuted },
                ];
                const currentTab = STATUS_TABS.find(t => t.value === statusTab) || STATUS_TABS[0];
                const tabColor = lfgMode ? THEME.textMuted : (currentTab.color || THEME.text);
                return (
                  <button
                    onClick={() => setShowMobileStatusPicker(true)}
                    className="press-effect"
                    style={{
                      flex: 1,
                      background: lfgMode ? `linear-gradient(135deg, ${THEME.omega}20, ${THEME.supe}20)` : `${tabColor}20`,
                      color: lfgMode ? THEME.text : tabColor,
                      border: `1px solid ${lfgMode ? THEME.border : tabColor}`,
                      borderRadius: '12px', padding: '10px 14px', fontSize: '14px', fontWeight: '600',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px'
                    }}
                  >
                    <span>{lfgMode ? '🎮 LFG Mode' : currentTab.label}</span>
                    <span style={{ opacity: 0.7, fontSize: '13px' }}>{lfgMode ? (stats?.lfg_games || 0) : currentTab.count} ▼</span>
                  </button>
                );
              })()}
              {/* Quick Actions */}
              <button onClick={() => setShowPicker(true)} className="press-effect" style={{ background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '12px', padding: '10px 14px', fontSize: '18px', cursor: 'pointer' }} title="Random">
                🎲
              </button>
              <button onClick={() => setShowQueueModal(true)} className="press-effect" style={{ background: queue.length > 0 ? `${THEME.accent}15` : THEME.bg, border: `1px solid ${queue.length > 0 ? THEME.accent : THEME.border}`, borderRadius: '12px', padding: '10px 14px', fontSize: '18px', cursor: 'pointer', position: 'relative' }} title="Play Queue">
                📋
                {queue.length > 0 && <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: THEME.accent, color: '#000', fontSize: '10px', fontWeight: '700', width: '18px', height: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{queue.length}</span>}
              </button>
              <button onClick={() => setShowDiscoverMenu(!showDiscoverMenu)} className="press-effect" style={{ background: showDiscoverMenu ? THEME.bgHover : THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '12px', padding: '10px 14px', fontSize: '18px', cursor: 'pointer', position: 'relative' }} title="Discover">
                🔍
              </button>
              {showDiscoverMenu && (
                <>
                  <div onClick={() => setShowDiscoverMenu(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} />
                  <div style={{ position: 'absolute', top: '100%', right: '16px', marginTop: '8px', background: THEME.bgCard, border: `1px solid ${THEME.border}`, borderRadius: '12px', padding: '8px', minWidth: '200px', zIndex: 999, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                    {[
                      { icon: '🧠', label: 'Smart Suggest', action: () => setShowSmartSuggest(true) },
                      { icon: '💎', label: 'Hidden Gems', action: () => setShowHiddenGems(true) },
                      { icon: '📊', label: 'Rating Comparison', action: () => setShowRatingComparison(true) },
                      { icon: '🕐', label: 'Recent Sessions', action: () => setShowLastTenPlayed(true) },
                      { icon: '🔥', label: 'Top 10 (30 Days)', action: () => setShowTopTen(true) },
                      { icon: '🆕', label: 'Recently Added', action: () => setShowRecentlyAdded(true) },
                      { icon: '⭐', label: 'Rate Unrated', action: () => setShowRateUnrated(true) },
                      { icon: '🏆', label: 'Achievement Races', action: () => setShowAchievementRaces && setShowAchievementRaces(true) },
                      { icon: '🎁', label: 'Steam Wishlist', action: () => setShowWishlist && setShowWishlist(true) },
                      { icon: '👥', label: 'Steam Friends', action: () => setShowFriendsActivity(true) },
                      { icon: '📺', label: 'Twitch Streams', action: () => setShowTwitchPanel(true) },
                    ].map(item => (
                      <button
                        key={item.label}
                        onClick={() => { item.action(); setShowDiscoverMenu(false); }}
                        className="press-effect"
                        style={{
                          width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 12px',
                          color: THEME.text, fontSize: '14px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px'
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>{item.icon}</span>
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          /* Desktop Header - Streamlined Option A */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Row 1: Logo + Branding + Squad + Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* Site Logos */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
                  <img src="/icons/icon.png" alt="jasons.fun" style={{ height: '44px', width: 'auto', borderRadius: '10px' }} />
                </a>
                <a href="https://600mmr.com" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', opacity: 0.5, transition: 'opacity 0.2s' }} title="600mmr.com" onMouseOver={e => e.currentTarget.style.opacity = 1} onMouseOut={e => e.currentTarget.style.opacity = 0.5}>
                  <img src="/600mmr-logo.png" alt="600mmr" style={{ height: '44px', width: 'auto', borderRadius: '10px' }} />
                </a>
                <a href="/clipper" style={{ display: 'flex', opacity: 0.5, transition: 'opacity 0.2s' }} title="Superior Clipper" onMouseOver={e => e.currentTarget.style.opacity = 1} onMouseOut={e => e.currentTarget.style.opacity = 0.5}>
                  <img src="/clipper/logo.png" alt="Superior Clipper" style={{ height: '44px', width: 'auto', borderRadius: '10px' }} />
                </a>
                <a href="/redline/" style={{ display: 'flex', opacity: 0.5, transition: 'opacity 0.2s' }} title="RedLine FC" onMouseOver={e => e.currentTarget.style.opacity = 1} onMouseOut={e => e.currentTarget.style.opacity = 0.5}>
                  <img src="/rlfc-logo.png" alt="RedLine FC" style={{ height: '44px', width: 'auto', borderRadius: '10px' }} />
                </a>
              </div>

              {/* Divider */}
              <div style={{ width: '1px', height: '28px', background: THEME.border }} />

              {/* Library Toggle */}
              <div style={{ display: 'flex', gap: '2px', background: THEME.bg, borderRadius: '8px', padding: '3px', border: `1px solid ${THEME.border}` }}>
                {[{ mode: 'coop', label: 'Co-op' }, { mode: user.username, label: 'My Library' }].map(({ mode, label }) => (
                  <button key={mode} onClick={() => setLibraryMode(mode)} className="press-effect" style={{
                    background: libraryMode === mode ? (mode === 'coop' ? `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})` : myColor) : 'transparent',
                    color: libraryMode === mode ? '#000' : THEME.textMuted, border: 'none', borderRadius: '6px', padding: '6px 14px',
                    fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap'
                  }}>{label}</button>
                ))}
              </div>

              {/* Squad Selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: THEME.bg, borderRadius: '8px', padding: '4px 8px', border: `1px solid ${THEME.border}` }}>
                {squads.length > 1 ? (
                  <select
                    value={currentSquad?.id || ''}
                    onChange={async (e) => {
                      const squadId = parseInt(e.target.value);
                      const squad = squads.find(s => s.id === squadId);
                      setCurrentSquad(squad);
                      if (squad) {
                        try {
                          const details = await api.get(`/api/squads/${squad.id}`);
                          setSquadMembers(details.members || []);
                        } catch (err) { console.error('Failed to load squad details:', err); }
                      }
                    }}
                    style={{
                      background: 'transparent', border: 'none', padding: '4px 8px',
                      color: THEME.text, fontSize: '13px', cursor: 'pointer', outline: 'none', fontWeight: '500'
                    }}
                  >
                    {squads.map(s => (
                      <option key={s.id} value={s.id}>{s.icon} {s.name}</option>
                    ))}
                  </select>
                ) : currentSquad ? (
                  <span style={{ padding: '4px 8px', fontSize: '13px', fontWeight: '500', color: THEME.text }}>{currentSquad.icon} {currentSquad.name}</span>
                ) : (
                  <span style={{ padding: '4px 8px', fontSize: '13px', color: THEME.textMuted }}>No squad</span>
                )}
                <button onClick={() => setShowSquadManager(true)} className="press-effect" style={{ background: 'transparent', border: 'none', padding: '4px', fontSize: '12px', cursor: 'pointer', color: THEME.textMuted }} title="Manage Squads">⚙️</button>
              </div>

              {/* Squad Members Status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {squadMembers.map(member => {
                  const status = steamStatus[member.username];
                  const memberColor = member.theme_color || THEME.text;
                  const isPlaying = status?.current_game_name;
                  return (
                    <div
                      key={member.id}
                      onClick={async () => {
                        if (isPlaying && status.game_in_db) { setDetailGame(status.game_in_db); }
                        else if (isPlaying && status.steam_store_url) { window.open(status.steam_store_url, '_blank'); }
                      }}
                      className="press-effect"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px',
                        background: isPlaying ? `${memberColor}15` : 'transparent',
                        borderRadius: '16px', cursor: isPlaying ? 'pointer' : 'default',
                        border: isPlaying ? `1px solid ${memberColor}40` : 'none'
                      }}
                      title={isPlaying ? `${member.display_name}: ${status.current_game_name}` : `${member.display_name}: Not playing`}
                    >
                      <div style={{ position: 'relative' }} onClick={(e) => { e.stopPropagation(); SoundService.modalOpen(); setProfileUserId(member.id); }}>
                        <Avatar src={member.avatar_url} name={member.display_name} size={24} color={memberColor} />
                        <div style={{
                          position: 'absolute', bottom: '-2px', right: '-2px', width: '8px', height: '8px',
                          borderRadius: '50%', background: isPlaying ? THEME.success : THEME.textDim,
                          border: `2px solid ${THEME.bgCard}`
                        }} />
                      </div>
                      {isPlaying && (
                        <span style={{ color: memberColor, fontSize: '12px', fontWeight: '600', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {status.current_game_name}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Spacer */}
              <div style={{ flex: 1 }} />

              {/* Quick Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {currentSquad && (
                  <>
                    <button onClick={() => setShowSquadChat(true)} className="press-effect" style={{ background: 'transparent', border: 'none', borderRadius: '8px', padding: '8px', fontSize: '16px', cursor: 'pointer', color: THEME.text }} title="Squad Chat">
                      💬
                    </button>
                    <button
                      onClick={async () => {
                        setCreatingVoiceHeader(true);
                        try {
                          const result = await api.post(`/api/integrations/discord/squad/${currentSquad.id}/voice`, {});
                          if (result.web_link) {
                            window.open(result.web_link, '_blank');
                            // Show different toast based on response type
                            if (result.is_static) {
                              addToast('success', 'Opening voice channel...');
                            } else if (result.is_existing) {
                              addToast('success', `Joining ${result.channel_name || 'voice channel'}...`);
                            } else {
                              addToast('success', `Created ${result.channel_name || 'voice channel'}!`);
                            }
                          } else if (result.error) {
                            addToast('error', result.error);
                          }
                        } catch (err) {
                          const msg = err.response?.data?.detail || err.message || 'Failed to create voice channel';
                          if (msg.includes('not linked') || msg.includes('Discord')) {
                            addToast('error', 'Squad not linked to Discord. Go to Squad Management → Discord to set up.');
                          } else {
                            addToast('error', msg);
                          }
                        } finally { setCreatingVoiceHeader(false); }
                      }}
                      disabled={creatingVoiceHeader}
                      className="press-effect"
                      style={{ background: creatingVoiceHeader ? THEME.bgHover : '#5865F230', border: '1px solid #5865F240', borderRadius: '8px', padding: '6px 10px', fontSize: '14px', cursor: creatingVoiceHeader ? 'not-allowed' : 'pointer', color: '#5865F2', display: 'flex', alignItems: 'center', gap: '6px' }}
                      title="Start Discord Voice Chat"
                    >
                      🔊 {creatingVoiceHeader ? '...' : 'Voice'}
                    </button>
                  </>
                )}
                {activeRemotePlay.length > 0 && (
                  <button
                    onClick={() => {
                      const session = activeRemotePlay[0];
                      if (confirm(`${session.host.display_name} is hosting "${session.game.name}"!\n\nOpen Steam to accept the invite when it arrives?`)) {
                        window.location.href = `steam://run/${session.game.appid}`;
                      }
                    }}
                    className="press-effect"
                    style={{ background: '#1a9fff20', border: '1px solid #1a9fff40', borderRadius: '8px', padding: '6px 10px', fontSize: '14px', cursor: 'pointer', color: '#1a9fff', display: 'flex', alignItems: 'center', gap: '6px', animation: 'pulse 2s infinite' }}
                    title={`${activeRemotePlay[0]?.host.display_name} is hosting Remote Play`}
                  >
                    <span style={{ fontSize: '8px', background: '#1a9fff', width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block' }} />
                    <span style={{ fontWeight: '600' }}>📡 Join</span>
                  </button>
                )}
                {twitchStreams.filter(s => s.is_live).length > 0 && (
                  <button onClick={() => setShowTwitchPanel(true)} className="press-effect" style={{ background: `${THEME.accent}20`, border: `1px solid ${THEME.accent}40`, borderRadius: '8px', padding: '6px 10px', fontSize: '14px', cursor: 'pointer', color: THEME.accent, display: 'flex', alignItems: 'center', gap: '6px', animation: 'pulse 2s infinite' }} title="Live Streams">
                    <span style={{ fontSize: '8px', background: '#ef4444', width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block' }} />
                    <span style={{ fontWeight: '600' }}>{twitchStreams.filter(s => s.is_live).length} LIVE</span>
                  </button>
                )}
                <button onClick={() => setShowUserDiscovery(true)} className="press-effect" style={{ background: 'transparent', border: 'none', borderRadius: '8px', padding: '8px', fontSize: '16px', cursor: 'pointer', color: THEME.info }} title="Find Users">
                  👥
                </button>
                <button onClick={() => { setShowNotificationsModal(true); loadActivities(); loadDirectMessages(); }} className="press-effect" style={{ background: 'transparent', border: 'none', borderRadius: '8px', padding: '8px', fontSize: '16px', cursor: 'pointer', position: 'relative' }} title="Notifications">
                  📬
                  {(activityCount + dmUnreadCount) > 0 && <span style={{ position: 'absolute', top: '0', right: '0', background: THEME.danger, color: '#fff', fontSize: '9px', fontWeight: '700', padding: '1px 4px', borderRadius: '8px', minWidth: '12px', textAlign: 'center' }}>{(activityCount + dmUnreadCount) > 9 ? '9+' : (activityCount + dmUnreadCount)}</span>}
                </button>
                <button onClick={() => setShowHelpCenter(true)} className="press-effect" style={{ background: 'transparent', border: 'none', borderRadius: '8px', padding: '8px', fontSize: '16px', cursor: 'pointer' }} title="Help">
                  ❓
                </button>
              </div>

              {/* Divider */}
              <div style={{ width: '1px', height: '28px', background: THEME.border }} />

              {/* User Avatar + Settings */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', borderRadius: '8px' }}>
                <div onClick={() => { SoundService.modalOpen(); setProfileUserId(user.id); }} className="press-effect" style={{ cursor: 'pointer' }} title="View Profile">
                  <Avatar src={user.avatar_url} name={user.display_name} size={32} color={myColor} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: myColor, fontWeight: '600', fontSize: '13px', lineHeight: '1.2' }}>{user.display_name}{user.is_founder && ` ${user.founder_icon || '⭐'}`}</span>
                  <span style={{ color: THEME.textMuted, fontSize: '11px', lineHeight: '1.2' }}>@{user.username}</span>
                </div>
                <button onClick={() => { SoundService.modalOpen(); setShowSettings(true); }} className="press-effect" style={{ background: THEME.bgHover, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '6px 10px', fontSize: '14px', cursor: 'pointer', color: THEME.textMuted, marginLeft: '4px' }} title="Settings">
                  ⚙️
                </button>
              </div>
            </div>

            {/* Row 2: Game Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {/* LFG Button */}
                <button
                  onClick={() => { SoundService.toggle(); setLfgMode(!lfgMode); if (!lfgMode) { setStatusTab('all'); setUserStatusFilters({}); } }}
                  className={`press-effect ${lfgMode ? 'lfg-active' : ''}`}
                  style={{
                    background: lfgMode ? `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})` : `linear-gradient(135deg, ${THEME.omega}40, ${THEME.supe}40)`,
                    color: lfgMode ? '#000' : THEME.text,
                    border: lfgMode ? '2px solid rgba(255,255,255,0.3)' : `1px solid ${THEME.accent}60`,
                    borderRadius: '20px', padding: '10px 18px', fontSize: '14px', fontWeight: '700',
                    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    boxShadow: lfgMode ? `0 2px 10px ${THEME.successGlow}` : 'none'
                  }}
                  title="Looking For Game"
                >
                  🎮 LFG {stats?.lfg_games > 0 && <span style={{ marginLeft: '4px', opacity: 0.8 }}>({stats.lfg_games})</span>}
                </button>

                {/* Status Tab Dropdown */}
                {(() => {
                  const STATUS_TABS = [
                    { value: 'all', label: 'All', count: stats?.total_games || 0 },
                    { value: 'played', label: 'Played', count: stats?.played_games || 0, color: THEME.success },
                    { value: 'wtp', label: 'WTP', count: stats?.wtp_games || 0, color: THEME.warning },
                    { value: 'shelved', label: 'Shelved', count: stats?.shelved_games || 0, color: THEME.textSecondary },
                    { value: 'never', label: 'Never', count: stats?.never_games || 0, color: THEME.danger },
                    { value: 'unplayed', label: 'Unplayed', count: stats?.unplayed_games || 0, color: THEME.textMuted },
                  ];
                  const currentTab = STATUS_TABS.find(t => t.value === statusTab) || STATUS_TABS[0];
                  const tabColor = lfgMode ? THEME.textMuted : (currentTab.color || THEME.text);
                  return (
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => setShowStatusDropdown && setShowStatusDropdown(!showStatusDropdown)}
                        className="press-effect"
                        style={{
                          background: lfgMode ? THEME.bg : `${tabColor}20`,
                          color: tabColor,
                          border: `1px solid ${lfgMode ? THEME.border : tabColor}`,
                          borderRadius: '20px', padding: '10px 18px', fontSize: '14px', fontWeight: '600',
                          cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px'
                        }}
                      >
                        {currentTab.label} <span style={{ opacity: 0.7 }}>{currentTab.count}</span>
                        <span style={{ fontSize: '10px', transition: 'transform 0.2s', transform: showStatusDropdown ? 'rotate(180deg)' : 'none' }}>▼</span>
                      </button>
                      {showStatusDropdown && (
                        <>
                          <div onClick={() => setShowStatusDropdown(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} />
                          <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '8px', background: THEME.bgCard, border: `1px solid ${THEME.border}`, borderRadius: '12px', padding: '8px', minWidth: '180px', zIndex: 1000, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                            {STATUS_TABS.map(tab => (
                              <button
                                key={tab.value}
                                onClick={() => { setStatusTab(tab.value); setLfgMode(false); setUserStatusFilters({}); setShowStatusDropdown(false); }}
                                className="press-effect"
                                style={{
                                  width: '100%', background: statusTab === tab.value ? `${tab.color || THEME.text}20` : 'transparent',
                                  border: 'none', borderRadius: '8px', padding: '10px 14px', color: tab.color || THEME.text,
                                  fontSize: '14px', fontWeight: statusTab === tab.value ? '700' : '500', cursor: 'pointer',
                                  textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                }}
                              >
                                <span>{tab.label}</span>
                                <span style={{ opacity: 0.6 }}>{tab.count}</span>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* View Toggle */}
                <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />

                {/* Random Button */}
                <button onClick={() => { SoundService.modalOpen(); setShowPicker(true); }} className="press-effect hover-lift" style={{ background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '10px', padding: '10px 16px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', boxShadow: `0 2px 10px ${THEME.successGlow}` }}>
                  🎲
                </button>

                {/* Queue Button */}
                <button onClick={() => setShowQueueModal(true)} className="press-effect" style={{ background: queue.length > 0 ? `${THEME.accent}20` : THEME.bgCard, border: `1px solid ${queue.length > 0 ? THEME.accent : THEME.border}`, borderRadius: '10px', padding: '10px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', color: queue.length > 0 ? THEME.accent : THEME.text, display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }} title="Play Queue">
                  📋 Queue {queue.length > 0 && <span style={{ background: THEME.accent, color: '#000', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '700' }}>{queue.length}</span>}
                </button>

                {/* Discover Dropdown */}
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setShowDiscoverMenu(!showDiscoverMenu)} className="press-effect" style={{ background: THEME.bgCard, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '10px 14px', color: THEME.text, fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    🔍
                    <span style={{ fontSize: '10px', transition: 'transform 0.2s', transform: showDiscoverMenu ? 'rotate(180deg)' : 'none' }}>▼</span>
                  </button>
                  {showDiscoverMenu && (
                    <>
                      <div onClick={() => setShowDiscoverMenu(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} />
                      <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '8px', background: THEME.bgCard, border: `1px solid ${THEME.border}`, borderRadius: '12px', padding: '8px', minWidth: '220px', zIndex: 1000, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                        <button onClick={() => { handleDiscover('smartSuggest'); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: `linear-gradient(90deg, ${THEME.omega}15, ${THEME.supe}15)`, border: `1px solid ${THEME.accent}30`, borderRadius: '8px', padding: '12px 16px', color: THEME.text, fontSize: '14px', fontWeight: '600', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '18px' }}>🎯</span> What Should We Play?
                        </button>
                        <div style={{ height: '1px', background: THEME.border, margin: '8px 0' }} />
                        <button onClick={() => { setShowLastTenPlayed(true); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 14px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>🕹️</span> Last 10 Played
                        </button>
                        <button onClick={() => { setShowTopTen(true); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 14px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>🏆</span> Top 10 (30 Days)
                        </button>
                        <button onClick={() => { setShowRecentlyAdded(true); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 14px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>✨</span> Recently Added
                        </button>
                        <button onClick={() => { setShowHiddenGems(true); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 14px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>💎</span> Hidden Gems
                        </button>
                        <button onClick={() => { setShowRateUnrated(true); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 14px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>⭐</span> Rate Unrated
                        </button>
                        <button onClick={() => { setShowGameNightScheduler(true); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 14px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>📅</span> Schedule Game Night
                        </button>
                        <div style={{ height: '1px', background: THEME.border, margin: '8px 0' }} />
                        <button onClick={() => { setShowRatingComparison(true); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 14px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>📊</span> Rating Comparison
                        </button>
                        <button onClick={() => { setShowGenreBreakdown(true); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 14px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>🎯</span> Genre Breakdown
                        </button>
                        <button onClick={() => { setShowYearReview(true); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 14px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>📅</span> Year in Review
                        </button>
                        <button onClick={() => { setShowBacklogBurndown(true); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 14px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>📉</span> Backlog Burndown
                        </button>
                        <div style={{ height: '1px', background: THEME.border, margin: '8px 0' }} />
                        <button onClick={() => { setShowQueueModal(true); setShowDiscoverMenu(false); }} className="press-effect" style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: '8px', padding: '10px 14px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>📋</span> Next Up Queue {queue.length > 0 && <span style={{ background: THEME.accent, color: '#000', padding: '2px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>{queue.length}</span>}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Spacer */}
                <div style={{ flex: 1, minWidth: '10px' }} />

                {/* Search */}
                <div style={{ position: 'relative', minWidth: '200px', maxWidth: '320px' }}>
                  <input
                    type="text"
                    placeholder="Search... (/)"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{
                      width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`,
                      borderRadius: '10px', padding: search ? '12px 36px 12px 38px' : '12px 16px 12px 38px', color: THEME.text,
                      fontSize: '14px', outline: 'none'
                    }}
                  />
                  <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: THEME.textDim, fontSize: '16px' }}>⌕</span>
                  {search && (
                    <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: THEME.textMuted, fontSize: '16px', cursor: 'pointer', padding: '4px', lineHeight: 1 }} title="Clear search">×</button>
                  )}
                </div>

                {/* Filters */}
                <button onClick={() => setShowFilters(true)} className="press-effect" style={{ background: activeFilterCount > 0 ? `${THEME.accent}20` : THEME.bg, border: `1px solid ${activeFilterCount > 0 ? THEME.accent : THEME.border}`, borderRadius: '10px', padding: '10px 16px', color: THEME.text, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  ≡ Filters {activeFilterCount > 0 && <span style={{ background: THEME.accent, color: '#000', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' }}>{activeFilterCount}</span>}
                </button>

                {/* Quick Presets Dropdown */}
                {filterPresets.length > 0 && (
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowPresetDropdown(!showPresetDropdown)}
                      className="press-effect"
                      style={{ background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '10px 12px', color: THEME.text, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      title="Quick load filter preset"
                    >
                      🏷️ <span style={{ fontSize: '10px' }}>▼</span>
                    </button>
                    {showPresetDropdown && (
                      <div
                        style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', background: THEME.bgCard, border: `1px solid ${THEME.border}`, borderRadius: '10px', boxShadow: `0 4px 12px ${THEME.shadowHeavy}`, zIndex: 100, minWidth: '160px', overflow: 'hidden' }}
                        onMouseLeave={() => setShowPresetDropdown(false)}
                      >
                        <div style={{ padding: '8px 12px', borderBottom: `1px solid ${THEME.border}`, fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Saved Presets</div>
                        {filterPresets.map(preset => (
                          <button
                            key={preset.id}
                            onClick={() => { loadFilterPreset(preset); setShowPresetDropdown(false); }}
                            style={{ width: '100%', background: 'transparent', border: 'none', padding: '10px 12px', color: THEME.text, fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = THEME.bgHover}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ color: THEME.accent }}>🏷️</span> {preset.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
        )}
      </header>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        user={user}
        onSync={handleSync}
        onSettings={() => setShowSettings(true)}
        onLogout={handleLogout}
        libraryMode={libraryMode}
        setLibraryMode={setLibraryMode}
        syncing={syncing}
        onDiscover={handleDiscover}
        onUserDiscovery={() => setShowUserDiscovery(true)}
        onAddGame={() => setShowAddGame(true)}
      />

      <SyncProgress syncStatus={syncStatus} onRetry={handleSync} />

      {/* Mobile: NOW PLAYING Bar - Dynamic based on squad members */}
      {isMobile && squadMembers.length > 0 && (
        <div style={{ padding: '8px 16px', background: THEME.bgCard, borderBottom: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: THEME.textDim, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>NOW</span>
          <div style={{ display: 'flex', gap: '8px', flex: 1, overflow: 'hidden' }}>
            {squadMembers.map(member => {
              const status = steamStatus[member.username];
              const memberColor = member.theme_color || THEME.success;
              return status?.current_game_name ? (
                <button key={member.username} onClick={() => { if (status.game_in_db) setDetailGame(status.game_in_db); else if (status.steam_store_url) window.open(status.steam_store_url, '_blank'); }} className="press-effect" style={{ background: `${memberColor}15`, border: `1px solid ${memberColor}40`, borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', maxWidth: squadMembers.length > 2 ? '30%' : '45%' }}>
                  <span style={{ fontSize: '12px' }}>{member.founder_icon || '🎮'}</span>
                  <span style={{ color: memberColor, fontSize: '12px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{status.current_game_name}</span>
                </button>
              ) : (
                <span key={member.username} style={{ color: memberColor, fontSize: '11px', opacity: 0.5, padding: '6px 0' }}>{member.founder_icon || '🎮'} —</span>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile: WTP NEXT Bar - Dynamic based on squad members */}
      {isMobile && Object.keys(recentRecs).some(k => recentRecs[k]) && (
        <div style={{ padding: '8px 16px', background: THEME.bgCard, borderBottom: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: THEME.textDim, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>NEXT</span>
          <div style={{ display: 'flex', gap: '8px', flex: 1, overflow: 'hidden' }}>
            {squadMembers.map(member => {
              const rec = recentRecs[member.username];
              if (!rec) return null;
              const memberColor = member.theme_color || THEME.success;
              return (
                <button key={member.username} onClick={async () => { let g = games.find(g => g.id === rec.game_id); if (!g) { try { g = await api.get(`/api/games/${rec.game_id}`); } catch (e) {} } if (g) setDetailGame(g); }} className="press-effect" style={{ background: `${memberColor}15`, border: `1px solid ${memberColor}40`, borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', maxWidth: squadMembers.length > 2 ? '30%' : '45%' }}>
                  <span style={{ fontSize: '12px' }}>{member.founder_icon || '🎮'}</span>
                  <span style={{ color: memberColor, fontSize: '12px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rec.game_name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastNotification toasts={toasts} onDismiss={dismissToast} onAction={handleToastAction} />

      {/* Mobile Status Tabs - Horizontal scrollable */}
      {isMobile && (
        <div className="hide-scrollbar" style={{ display: 'flex', gap: '8px', padding: '10px 16px', overflowX: 'auto', background: THEME.bgCard, borderBottom: `1px solid ${THEME.border}` }}>
          <button
            onClick={() => { setLfgMode(!lfgMode); if (!lfgMode) { setStatusTab('all'); setUserStatusFilters({}); } }}
            className="press-effect"
            style={{
              background: lfgMode ? `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})` : THEME.bg,
              color: lfgMode ? '#000' : THEME.textMuted,
              border: `1px solid ${lfgMode ? 'transparent' : THEME.border}`,
              borderRadius: '16px', padding: '6px 12px', fontSize: '12px', fontWeight: '600',
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0
            }}
          >
            🎮 LFG {stats?.lfg_games > 0 && <span style={{ marginLeft: '4px', opacity: 0.8 }}>({stats.lfg_games})</span>}
          </button>
          {[
            { value: 'all', label: 'All', count: stats?.total_games || 0 },
            { value: 'played', label: 'Played', count: stats?.played_games || 0, color: THEME.success },
            { value: 'wtp', label: 'WTP', count: stats?.wtp_games || 0, color: THEME.warning },
            { value: 'never', label: 'Never', count: stats?.never_games || 0, color: THEME.danger },
            { value: 'unplayed', label: 'Unplayed', count: stats?.unplayed_games || 0, color: THEME.textMuted },
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => { setStatusTab(tab.value); setLfgMode(false); setUserStatusFilters({}); }}
              className="press-effect"
              style={{
                background: (!lfgMode && statusTab === tab.value) ? (tab.color || THEME.text) : THEME.bg,
                color: (!lfgMode && statusTab === tab.value) ? '#000' : (tab.color || THEME.textMuted),
                border: `1px solid ${(!lfgMode && statusTab === tab.value) ? (tab.color || THEME.text) : THEME.border}`,
                borderRadius: '16px', padding: '6px 12px', fontSize: '12px', fontWeight: '600',
                cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0
              }}
            >
              {tab.label} <span style={{ opacity: 0.7 }}>{tab.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Filter Bar - Mobile only */}
      {isMobile && (
        <div style={{ padding: '10px 16px', display: 'flex', gap: '8px', alignItems: 'center', borderBottom: `1px solid ${THEME.border}`, background: THEME.bgCard }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '1', minWidth: '80px' }}>
            <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: search ? '10px 32px 10px 32px' : '10px 12px 10px 32px', color: THEME.text, fontSize: '13px', outline: 'none' }} />
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: THEME.textDim, fontSize: '13px' }}>⌕</span>
            {search && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: THEME.textMuted, fontSize: '14px', cursor: 'pointer', padding: '4px', lineHeight: 1 }} title="Clear search">×</button>
            )}
          </div>
          {/* Filters */}
          <button onClick={() => setShowMobileFilters(true)} className="press-effect" style={{ background: activeFilterCount > 0 ? `${THEME.omega}20` : THEME.bg, border: `1px solid ${activeFilterCount > 0 ? THEME.omega : THEME.border}`, borderRadius: '10px', padding: '10px 12px', color: THEME.text, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
            ≡ {activeFilterCount > 0 && <span style={{ background: THEME.omega, color: '#000', padding: '1px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: '700' }}>{activeFilterCount}</span>}
          </button>
          {/* Random */}
          <button onClick={() => setShowPicker(true)} className="press-effect" style={{ background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '10px', padding: '10px 12px', fontSize: '14px', cursor: 'pointer' }}>
            🎲
          </button>
        </div>
      )}

      {/* Pinned Games Bar */}
      {pinnedGames.length > 0 && (
        <div style={{ padding: isMobile ? '12px 16px' : '12px 24px', background: THEME.bg, borderBottom: `1px solid ${THEME.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ color: THEME.textMuted, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>📌 Pinned</span>
            <div style={{ display: 'flex', gap: '8px', flex: 1, overflowX: 'auto' }} className="hide-scrollbar">
              {pinnedGames.map(pin => (
                <button
                  key={pin.id}
                  onClick={() => setDetailGame(pin.game)}
                  className="press-effect"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: THEME.bgCard, border: `1px solid ${THEME.border}`,
                    borderRadius: '8px', padding: '6px 12px', cursor: 'pointer',
                    whiteSpace: 'nowrap', flexShrink: 0
                  }}
                >
                  <img src={pin.game.header_image} alt="" style={{ width: '32px', height: '18px', objectFit: 'cover', borderRadius: '4px' }} />
                  <span style={{ color: THEME.text, fontSize: '13px', fontWeight: '500', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pin.game.name}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePinGame(pin.game.id); }}
                    style={{ background: 'transparent', border: 'none', color: THEME.textDim, fontSize: '12px', cursor: 'pointer', padding: '2px' }}
                    title="Unpin"
                  >
                    ✕
                  </button>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Auto Session Detection Banner */}
      <AutoSessionsBanner onConfirm={loadGames} onDismiss={() => {}} />

      {/* Game Count */}
      <div style={{ padding: isMobile ? '10px 16px' : '10px 24px', color: THEME.textMuted, fontSize: isMobile ? '12px' : '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          Showing <strong style={{ color: THEME.text }}>{games.length}</strong>
          {totalGames > games.length && <> of <strong style={{ color: THEME.text }}>{totalGames}</strong></>} games
        </span>
      </div>

      {/* Games Grid/List */}
      {viewMode === 'list' ? (
        <div style={{ padding: isMobile ? '0 12px 120px' : '0 24px 120px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {!isMobile && (
            <div style={{ display: 'grid', gridTemplateColumns: '32px 28px 52px 1fr 90px 70px 45px 70px 130px 90px', gap: '12px', padding: '10px 16px', color: THEME.textDim, fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <span></span><span>★</span><span></span><span>Game</span><span>Type</span><span>Ratings</span><span title="Compatibility Score">Match</span><span>Time</span><span>Status</span><span>Actions</span>
            </div>
          )}
          {games.map(game => <GameCard key={game.id} game={game} currentUser={user} squadMembers={squadMembers} onUpdate={loadGames} onAddSession={setSessionGame} onShowDetail={setDetailGame} viewMode={isMobile ? 'cards' : 'list'} isSelected={selectedGames.has(game.id)} onToggleSelect={toggleGameSelect} />)}
        </div>
      ) : viewMode === 'compact' ? (
        <div style={{ padding: isMobile ? '0 12px 120px' : '0 24px 120px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(180px, 1fr))', gap: isMobile ? '12px' : '14px' }}>
          {games.map(game => <GameCard key={game.id} game={game} currentUser={user} squadMembers={squadMembers} onUpdate={loadGames} onAddSession={setSessionGame} onShowDetail={setDetailGame} viewMode={isMobile ? 'cards' : 'compact'} isSelected={selectedGames.has(game.id)} onToggleSelect={toggleGameSelect} />)}
        </div>
      ) : (
        <div style={{ padding: isMobile ? '0 12px 120px' : '0 24px 120px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: isMobile ? '12px' : '20px' }}>
          {games.map(game => <GameCard key={game.id} game={game} currentUser={user} squadMembers={squadMembers} onUpdate={loadGames} onAddSession={setSessionGame} onShowDetail={setDetailGame} viewMode="cards" isSelected={selectedGames.has(game.id)} onToggleSelect={toggleGameSelect} />)}
        </div>
      )}

      {games.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px 24px', color: THEME.textMuted }}>
          <div style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.3 }}>🎮</div>
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>No games found</p>
          <p style={{ fontSize: '14px', color: THEME.textDim }}>Try adjusting your filters or sync your Steam library</p>
        </div>
      )}

      <BulkActions selectedCount={selectedGames.size} onBulkStatus={handleBulkStatus} onClearSelection={() => setSelectedGames(new Set())} />

      {/* Modals */}
      <FilterDrawer isOpen={showFilters} onClose={() => setShowFilters(false)} userStatusFilters={userStatusFilters} setUserStatusFilters={setUserStatusFilters} userFavoritesFilters={userFavoritesFilters} setUserFavoritesFilters={setUserFavoritesFilters} userRecommendationsFilters={userRecommendationsFilters} setUserRecommendationsFilters={setUserRecommendationsFilters} typeFilter={typeFilter} setTypeFilter={setTypeFilter} sortBy={sortBy} setSortBy={setSortBy} onlineOnly={onlineOnly} setOnlineOnly={setOnlineOnly} controllerFilter={controllerFilter} setControllerFilter={setControllerFilter} tagFilter={tagFilter} setTagFilter={setTagFilter} allTags={allTags} unratedOnly={unratedOnly} setUnratedOnly={setUnratedOnly} presets={filterPresets} onSavePreset={saveFilterPreset} onLoadPreset={loadFilterPreset} onDeletePreset={deleteFilterPreset} squadMembers={squadMembers} />
      <AnimatePresence>
        {showPicker && <RandomPicker onClose={() => setShowPicker(false)} />}
      </AnimatePresence>
      {sessionGame && <AddSessionModal game={sessionGame} onClose={() => setSessionGame(null)} onSave={loadGames} />}
      <AnimatePresence>
        {showSettings && <SettingsModal user={user} currentSquad={currentSquad} onClose={() => setShowSettings(false)} onUpdate={u => setUser(prev => ({ ...prev, ...u }))} savedCredentials={savedCredentials} onSync={handleSync} onLogout={handleLogout} syncing={syncing} onThemeChange={(mode, accent) => { updateTheme(mode, accent); }} />}
      </AnimatePresence>
      <AnimatePresence>
        {detailGame && <GameDetailModal game={detailGame} currentUser={user} squadMembers={squadMembers} onClose={() => setDetailGame(null)} onUpdate={() => { loadGames(); loadRecentRecs(); }} />}
      </AnimatePresence>
      {showTour && <TourOverlay step={tourStep} onNext={handleTourNext} onSkip={handleTourSkip} onClose={handleTourSkip} totalSteps={TOUR_STEPS.length} />}
      {showStatsDashboard && <StatsDashboard stats={stats} onClose={() => setShowStatsDashboard(false)} />}
      <NotificationsModal isOpen={showNotificationsModal} onClose={() => setShowNotificationsModal(false)} activeTab={notificationsTab} setActiveTab={setNotificationsTab} activities={activities} activityCount={activityCount} dmMessages={directMessages} dmUnreadCount={dmUnreadCount} onMarkActivitiesSeen={markActivitiesSeen} onActivityClick={handleActivityClick} onMatchStatus={handleMatchStatus} onSendDM={sendDirectMessage} onMarkDMsRead={markDMsRead} currentUser={user} squadMembers={squadMembers} />

      {/* Discovery Modals */}
      {showLastTenPlayed && <LastTenPlayedModal onClose={() => setShowLastTenPlayed(false)} onShowDetail={setDetailGame} />}
      {showTopTen && <TopTenModal onClose={() => setShowTopTen(false)} onShowDetail={setDetailGame} />}
      {showRecentlyAdded && <RecentlyAddedModal onClose={() => setShowRecentlyAdded(false)} onShowDetail={setDetailGame} />}
      {showHiddenGems && <HiddenGemsModal onClose={() => setShowHiddenGems(false)} onShowDetail={setDetailGame} />}
      {showRateUnrated && <RateUnratedModal onClose={() => setShowRateUnrated(false)} onUpdate={loadGames} currentUser={user} />}
      {showRatingComparison && <RatingComparisonModal onClose={() => setShowRatingComparison(false)} />}
      {showGenreBreakdown && <GenreBreakdownModal onClose={() => setShowGenreBreakdown(false)} />}
      {showYearReview && <YearReviewModal onClose={() => setShowYearReview(false)} year={yearReviewYear} />}
      {showQueueModal && <QueueModal onClose={() => setShowQueueModal(false)} queue={queue} onRemove={removeFromQueue} onReorder={loadQueue} onShowDetail={(g) => { setShowQueueModal(false); setDetailGame(g); }} currentSquad={currentSquad} currentUser={user} />}
      {showBacklogBurndown && <BacklogBurndownModal onClose={() => setShowBacklogBurndown(false)} />}
      {showSmartSuggest && <SmartSuggestModal onClose={() => setShowSmartSuggest(false)} suggestions={smartSuggestions} onShowDetail={(g) => { setShowSmartSuggest(false); setDetailGame(g); }} />}
      <AnimatePresence>
        {showAddGame && <AddGameModal onClose={() => setShowAddGame(false)} onGameAdded={loadGames} />}
      </AnimatePresence>
      <AnimatePresence>
        {showGameNightScheduler && <GameNightSchedulerModal onClose={() => setShowGameNightScheduler(false)} squads={squads} currentSquad={currentSquad} />}
      </AnimatePresence>
      <AnimatePresence>
        {profileUserId && <UserProfileModal userId={profileUserId} onClose={() => setProfileUserId(null)} currentUser={user} onShowGame={async (gameId) => { setProfileUserId(null); const g = await api.get(`/api/games/${gameId}`); setDetailGame(g); }} />}
      </AnimatePresence>
      {showKeyboardHelp && <KeyboardHelpModal onClose={() => setShowKeyboardHelp(false)} />}
      {showHelpCenter && <HelpCenterModal onClose={() => setShowHelpCenter(false)} onStartTour={() => { setTourStep(0); setShowTour(true); }} />}
      {showSquadChat && currentSquad && <SquadChatModal squad={currentSquad} currentUser={user} onClose={() => setShowSquadChat(false)} />}
      {showSquadManager && <SquadManagementModal squads={squads} currentSquad={currentSquad} currentUser={user} onClose={() => setShowSquadManager(false)} onSquadsChange={setSquads} onSquadSelect={async (squad) => { setCurrentSquad(squad); if (squad) { try { const details = await api.get(`/api/squads/${squad.id}`); setSquadMembers(details.members || []); } catch (err) { console.error('Failed to load squad:', err); } } }} />}
      {showUserDiscovery && <UserDiscoveryModal currentUser={user} squads={squads} onClose={() => setShowUserDiscovery(false)} onRefreshInvites={async () => { const userSquads = await api.get('/api/squads'); setSquads(userSquads); }} />}

      {/* Steam Friends Activity Modal */}
      {showFriendsActivity && <FriendsActivityModal onClose={() => setShowFriendsActivity(false)} />}

      {/* Steam Wishlist Modal */}
      {showWishlist && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)' }} onClick={() => setShowWishlist(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} style={{ background: THEME.bgCard, borderRadius: '20px', padding: '24px', width: '95%', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto', border: `1px solid ${THEME.border}` }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: THEME.text, fontSize: '20px', fontWeight: '700' }}>🎁 Steam Wishlist</h2>
              <button onClick={() => setShowWishlist(false)} style={{ background: THEME.bgHover, border: 'none', color: THEME.textMuted, width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer' }}>&times;</button>
            </div>
            <button onClick={async () => { try { const result = await api.post('/api/integrations/steam/wishlist/sync', {}); const items = await api.get('/api/integrations/steam/wishlist'); setWishlistItems(items.items || []); } catch (err) { console.error(err); } }} style={{ width: '100%', background: '#1a9fff', color: '#fff', border: 'none', borderRadius: '10px', padding: '12px', marginBottom: '16px', fontWeight: '600', cursor: 'pointer' }}>🔄 Sync from Steam</button>
            {wishlistItems.length === 0 ? (
              <p style={{ color: THEME.textMuted, textAlign: 'center', padding: '40px' }}>No wishlist items synced yet. Click sync above!</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {wishlistItems.map(item => (
                  <div key={item.appid} style={{ display: 'flex', gap: '12px', padding: '12px', background: THEME.bg, borderRadius: '10px', border: `1px solid ${THEME.border}` }}>
                    <img src={item.header_image} alt="" style={{ width: '120px', height: '45px', borderRadius: '6px', objectFit: 'cover' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ color: THEME.text, fontWeight: '600', fontSize: '14px' }}>{item.name}</div>
                      <div style={{ color: THEME.textMuted, fontSize: '12px', marginTop: '4px' }}>
                        {item.discount_percent > 0 && <span style={{ color: THEME.success, fontWeight: '600' }}>-{item.discount_percent}% </span>}
                        {item.current_price ? `$${(item.current_price / 100).toFixed(2)}` : 'Price TBD'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* Friends Playing Modal */}
      {showFriendsPlaying && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)' }} onClick={() => setShowFriendsPlaying(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} style={{ background: THEME.bgCard, borderRadius: '20px', padding: '24px', width: '95%', maxWidth: '500px', maxHeight: '80vh', overflow: 'auto', border: `1px solid ${THEME.border}` }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: THEME.text, fontSize: '20px', fontWeight: '700' }}>👥 Friends Playing</h2>
              <button onClick={() => setShowFriendsPlaying(false)} style={{ background: THEME.bgHover, border: 'none', color: THEME.textMuted, width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer' }}>&times;</button>
            </div>
            <button onClick={async () => { try { const result = await api.get('/api/integrations/steam/friends'); setFriendsPlaying(result.friends || []); } catch (err) { console.error(err); } }} style={{ width: '100%', background: THEME.accent, color: '#000', border: 'none', borderRadius: '10px', padding: '12px', marginBottom: '16px', fontWeight: '600', cursor: 'pointer' }}>🔄 Refresh</button>
            {friendsPlaying.length === 0 ? (
              <p style={{ color: THEME.textMuted, textAlign: 'center', padding: '40px' }}>Click refresh to see what your Steam friends are playing!</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {friendsPlaying.filter(f => f.is_playing).map(friend => (
                  <div key={friend.steam_id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: THEME.bg, borderRadius: '10px', border: `1px solid ${THEME.success}40` }}>
                    <img src={friend.avatar} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ color: THEME.text, fontWeight: '600', fontSize: '14px' }}>{friend.name}</div>
                      <div style={{ color: THEME.success, fontSize: '12px' }}>🎮 {friend.current_game_name}</div>
                    </div>
                  </div>
                ))}
                {friendsPlaying.filter(f => !f.is_playing && f.persona_state > 0).slice(0, 10).map(friend => (
                  <div key={friend.steam_id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', background: THEME.bg, borderRadius: '8px', opacity: 0.7 }}>
                    <img src={friend.avatar} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                    <div style={{ color: THEME.textMuted, fontSize: '13px' }}>{friend.name} - Online</div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* Achievement Races Modal */}
      {showAchievementRaces && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)' }} onClick={() => setShowAchievementRaces(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} style={{ background: THEME.bgCard, borderRadius: '20px', padding: '24px', width: '95%', maxWidth: '500px', maxHeight: '80vh', overflow: 'auto', border: `1px solid ${THEME.border}` }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: THEME.text, fontSize: '20px', fontWeight: '700' }}>🏆 Achievement Races</h2>
              <button onClick={() => setShowAchievementRaces(false)} style={{ background: THEME.bgHover, border: 'none', color: THEME.textMuted, width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer' }}>&times;</button>
            </div>
            <p style={{ color: THEME.textMuted, fontSize: '14px', marginBottom: '16px' }}>Race to complete achievements with your squad! Start a race from any game's detail page.</p>
            <button onClick={async () => { try { const result = await api.get('/api/integrations/achievements/races'); setAchievementRaces(result.races || []); } catch (err) { console.error(err); } }} style={{ width: '100%', background: THEME.accent, color: '#000', border: 'none', borderRadius: '10px', padding: '12px', marginBottom: '16px', fontWeight: '600', cursor: 'pointer' }}>🔄 Load Active Races</button>
            {achievementRaces.length === 0 ? (
              <p style={{ color: THEME.textMuted, textAlign: 'center', padding: '40px' }}>No active races. Start one from a game's detail page!</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {achievementRaces.map(race => (
                  <div key={race.id} style={{ padding: '14px', background: THEME.bg, borderRadius: '10px', border: `1px solid ${THEME.accent}40` }}>
                    <div style={{ color: THEME.text, fontWeight: '600', marginBottom: '6px' }}>{race.game_name}</div>
                    <div style={{ color: THEME.textMuted, fontSize: '12px' }}>Type: {race.target_type} • Started: {new Date(race.started_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* Twitch Live Panel */}
      {showTwitchPanel && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
          onClick={() => setShowTwitchPanel(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            style={{ background: THEME.bgCard, borderRadius: '20px', padding: '24px', width: '95%', maxWidth: '500px', maxHeight: '80vh', overflow: 'auto', border: `1px solid ${THEME.border}` }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '24px' }}>📺</span>
                <h2 style={{ margin: 0, color: THEME.text, fontSize: '20px', fontWeight: '700' }}>Live on Twitch</h2>
              </div>
              <button onClick={() => setShowTwitchPanel(false)} style={{ background: THEME.bgHover, border: 'none', color: THEME.textMuted, width: '32px', height: '32px', borderRadius: '8px', fontSize: '18px', cursor: 'pointer' }}>&times;</button>
            </div>
            {twitchStreams.filter(s => s.is_live).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: THEME.textMuted }}>
                <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>😴</span>
                No one is streaming right now
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {twitchStreams.filter(s => s.is_live).map(stream => (
                  <a
                    key={stream.username}
                    href={stream.stream_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', background: THEME.bg, borderRadius: '14px', padding: '14px', border: `1px solid ${stream.color || THEME.accent}40`, display: 'flex', gap: '14px' }}
                  >
                    {stream.thumbnail_url && (
                      <img src={stream.thumbnail_url} alt="" style={{ width: '120px', height: '68px', borderRadius: '8px', objectFit: 'cover' }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Avatar src={stream.avatar_url} name={stream.display_name} size={24} color={stream.color || THEME.accent} />
                        <span style={{ color: stream.color || THEME.accent, fontWeight: '600', fontSize: '14px' }}>{stream.display_name}</span>
                        <span style={{ background: '#ef4444', color: '#fff', fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px' }}>LIVE</span>
                      </div>
                      <div style={{ color: THEME.text, fontSize: '13px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px' }}>{stream.title || 'No title'}</div>
                      <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: THEME.textMuted }}>
                        <span>🎮 {stream.game_name}</span>
                        <span>👁️ {stream.viewer_count.toLocaleString()} viewers</span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* Command Palette */}
      <AnimatePresence>
        {showCommandPalette && (
          <CommandPalette
          onClose={() => setShowCommandPalette(false)}
          commands={[
            // Navigation
            { id: 'nav-all', icon: '📚', label: 'Go to All Games', category: 'Navigation', keywords: ['all', 'games', 'library'], action: () => { setStatusTab('all'); setLfgMode(false); } },
            { id: 'nav-played', icon: '✅', label: 'Go to Played', category: 'Navigation', keywords: ['played', 'finished'], action: () => { setStatusTab('played'); setLfgMode(false); } },
            { id: 'nav-wtp', icon: '🎯', label: 'Go to Want to Play', category: 'Navigation', shortcut: 'L', keywords: ['wtp', 'want', 'play'], action: () => { setStatusTab('wtp'); setLfgMode(false); } },
            { id: 'nav-shelved', icon: '📦', label: 'Go to Shelved', category: 'Navigation', keywords: ['shelved', 'paused'], action: () => { setStatusTab('shelved'); setLfgMode(false); } },
            { id: 'nav-unplayed', icon: '🆕', label: 'Go to Unplayed', category: 'Navigation', keywords: ['unplayed', 'new'], action: () => { setStatusTab('unplayed'); setLfgMode(false); } },
            { id: 'nav-never', icon: '🚫', label: 'Go to Never Again', category: 'Navigation', keywords: ['never'], action: () => { setStatusTab('never'); setLfgMode(false); } },
            // Actions
            { id: 'act-lfg', icon: '🎮', label: 'Toggle LFG Mode', category: 'Actions', shortcut: 'L', keywords: ['lfg', 'looking', 'game'], action: () => setLfgMode(prev => !prev) },
            { id: 'act-random', icon: '🎲', label: 'Pick Random Game', category: 'Actions', shortcut: 'R', keywords: ['random', 'dice', 'pick'], action: () => setShowPicker(true) },
            { id: 'act-sync', icon: '🔄', label: 'Sync Steam Library', category: 'Actions', keywords: ['sync', 'steam', 'refresh'], action: () => handleSync() },
            { id: 'act-search', icon: '🔍', label: 'Focus Search', category: 'Actions', shortcut: '/', keywords: ['search', 'find'], action: () => document.querySelector('input[placeholder*="Search"]')?.focus() },
            { id: 'act-filters', icon: '≡', label: 'Open Filters', category: 'Actions', shortcut: 'F', keywords: ['filter', 'sort'], action: () => setShowFilters(true) },
            // Discover
            { id: 'disc-smart', icon: '🎯', label: 'Smart Suggest', category: 'Discover', shortcut: 'W', keywords: ['smart', 'suggest', 'what'], action: () => setShowSmartSuggest(true) },
            { id: 'disc-recent', icon: '🕹️', label: 'Last 10 Played', category: 'Discover', keywords: ['recent', 'last', 'played'], action: () => setShowLastTenPlayed(true) },
            { id: 'disc-top', icon: '🏆', label: 'Top 10 (30 Days)', category: 'Discover', keywords: ['top', 'best', 'playtime'], action: () => setShowTopTen(true) },
            { id: 'disc-added', icon: '✨', label: 'Recently Added', category: 'Discover', keywords: ['recently', 'added', 'new'], action: () => setShowRecentlyAdded(true) },
            { id: 'disc-gems', icon: '💎', label: 'Hidden Gems', category: 'Discover', keywords: ['hidden', 'gems', 'underrated'], action: () => setShowHiddenGems(true) },
            { id: 'disc-rate', icon: '⭐', label: 'Rate Unrated Games', category: 'Discover', keywords: ['rate', 'unrated', 'review'], action: () => setShowRateUnrated(true) },
            { id: 'disc-queue', icon: '📋', label: 'View Next Up Queue', category: 'Discover', keywords: ['queue', 'next', 'up'], action: () => setShowQueueModal(true) },
            { id: 'disc-schedule', icon: '📅', label: 'Schedule Game Night', category: 'Discover', keywords: ['schedule', 'game night', 'calendar'], action: () => setShowGameNightScheduler(true) },
            // Squad
            { id: 'squad-chat', icon: '💬', label: 'Open Squad Chat', category: 'Squad', keywords: ['chat', 'message'], action: () => currentSquad && setShowSquadChat(true) },
            { id: 'squad-voice', icon: '🔊', label: 'Start Voice Chat', category: 'Squad', keywords: ['voice', 'discord', 'call', 'talk'], action: async () => {
              if (!currentSquad) { addToast('error', 'Select a squad first'); return; }
              try {
                const result = await api.post(`/api/integrations/discord/squad/${currentSquad.id}/voice`, { name: `${currentSquad.name} Gaming` });
                if (result.web_link) { window.open(result.web_link, '_blank'); addToast('success', 'Voice channel created!'); }
              } catch (err) { addToast('error', err.response?.data?.detail || 'Squad not linked to Discord'); }
            }},
            { id: 'squad-manage', icon: '⚙️', label: 'Manage Squads', category: 'Squad', keywords: ['squad', 'manage', 'create', 'join'], action: () => setShowSquadManager(true) },
            { id: 'squad-users', icon: '👥', label: 'Find Users', category: 'Squad', keywords: ['users', 'invite', 'find'], action: () => setShowUserDiscovery(true) },
            // View
            { id: 'view-cards', icon: '🃏', label: 'Card View', category: 'View', keywords: ['cards', 'grid'], action: () => setViewMode('cards') },
            { id: 'view-list', icon: '📋', label: 'List View', category: 'View', keywords: ['list'], action: () => setViewMode('list') },
            { id: 'view-compact', icon: '📑', label: 'Compact View', category: 'View', keywords: ['compact', 'dense'], action: () => setViewMode('compact') },
            { id: 'view-coop', icon: '🤝', label: 'Switch to Co-op Library', category: 'View', keywords: ['coop', 'shared'], action: () => setLibraryMode('coop') },
            { id: 'view-personal', icon: '👤', label: 'Switch to My Library', category: 'View', keywords: ['my', 'personal', 'library'], action: () => setLibraryMode(user?.username) },
            // Settings
            { id: 'set-settings', icon: '⚙️', label: 'Open Settings', category: 'Settings', keywords: ['settings', 'preferences'], action: () => setShowSettings(true) },
            { id: 'add-game', icon: '➕', label: 'Add Non-Steam Game', category: 'Actions', keywords: ['add', 'game', 'igdb', 'manual', 'non-steam'], action: () => setShowAddGame(true) },
            { id: 'set-stats', icon: '📊', label: 'View Stats Dashboard', category: 'Settings', shortcut: 'D', keywords: ['stats', 'dashboard', 'analytics'], action: () => setShowStatsDashboard(true) },
            { id: 'set-notif', icon: '📬', label: 'View Notifications', category: 'Settings', keywords: ['notifications', 'activity', 'messages'], action: () => { setShowNotificationsModal(true); loadActivities(); loadDirectMessages(); } },
            { id: 'set-help', icon: '❓', label: 'Open Help Center', category: 'Settings', shortcut: 'H', keywords: ['help', 'guide', 'faq'], action: () => setShowHelpCenter(true) },
            { id: 'set-shortcuts', icon: '⌨️', label: 'Keyboard Shortcuts', category: 'Settings', shortcut: '?', keywords: ['keyboard', 'shortcuts', 'keys'], action: () => setShowKeyboardHelp(true) },
            // Filter Presets (dynamically added)
            ...filterPresets.map((preset, idx) => ({
              id: `preset-${preset.id}`,
              icon: '🏷️',
              label: `Load: ${preset.name}`,
              category: 'Presets',
              keywords: ['preset', 'filter', preset.name.toLowerCase()],
              action: () => loadFilterPreset(preset)
            })),
            ...(filterPresets.length > 0 ? [] : [{ id: 'preset-none', icon: '🏷️', label: 'No saved presets', category: 'Presets', keywords: ['preset'], action: () => setShowFilters(true) }]),
          ]}
        />
        )}
      </AnimatePresence>

      {/* Mobile Components */}
      {isMobile && (
        <>
          <MobileBottomNav
            onHome={() => { setMobileNavTab('home'); setLfgMode(false); setStatusTab('all'); }}
            onQueue={() => { setMobileNavTab('queue'); setShowQueueModal(true); }}
            onRandom={() => setShowPicker(true)}
            onStats={() => { setMobileNavTab('stats'); setShowStatsDashboard(true); }}
            onMore={() => setShowMobileFilters(true)}
            activeTab={mobileNavTab}
            queueCount={queue.length}
          />
          <MobileStatusPicker
            isOpen={showMobileStatusPicker}
            onClose={() => setShowMobileStatusPicker(false)}
            statusTab={statusTab}
            onStatusChange={(tab) => { setStatusTab(tab); setLfgMode(false); setUserStatusFilters({}); }}
            lfgMode={lfgMode}
            onLfgToggle={() => { setLfgMode(!lfgMode); if (!lfgMode) { setStatusTab('all'); setUserStatusFilters({}); } }}
            stats={stats}
          />
          <MobileFilterSheet
            isOpen={showMobileFilters}
            onClose={() => setShowMobileFilters(false)}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            sortBy={sortBy}
            setSortBy={setSortBy}
            onlineOnly={onlineOnly}
            setOnlineOnly={setOnlineOnly}
            controllerFilter={controllerFilter}
            setControllerFilter={setControllerFilter}
            unratedOnly={unratedOnly}
            setUnratedOnly={setUnratedOnly}
            userFavoritesFilters={userFavoritesFilters}
            setUserFavoritesFilters={setUserFavoritesFilters}
            userRecommendationsFilters={userRecommendationsFilters}
            setUserRecommendationsFilters={setUserRecommendationsFilters}
            squadMembers={squadMembers}
          />
        </>
      )}
    </div>
  );
}
